import { FormEvent, useState } from "react";
import { useSnackbar } from "notistack";
import { Link, useNavigate } from "react-router-dom";
import { IcpSampleManager } from "../components/IcpSampleManager";
import { StatusLine } from "../components/StatusLine";
import { useCreateIcp } from "../hooks/useIcps";

type Stage = "form" | "samples";

const NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

export function IcpCreate() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const create = useCreateIcp();

  const [stage, setStage] = useState<Stage>("form");
  const [createdName, setCreatedName] = useState<string | null>(null);
  const [createdDisplayName, setCreatedDisplayName] = useState<string>("");

  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [context, setContext] = useState("");
  const [description, setDescription] = useState("");
  const [minScore, setMinScore] = useState("50");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!NAME_PATTERN.test(trimmedName)) {
      setError(
        "Slug must start with a lowercase letter and contain only lowercase letters, digits, and underscores.",
      );
      return;
    }
    const trimmedDisplay = displayName.trim();
    if (!trimmedDisplay) {
      setError("Display name is required.");
      return;
    }
    const trimmedContext = context.trim();
    if (!trimmedContext) {
      setError("Context is required — this is the AI-facing intent.");
      return;
    }
    const parsedScore = Number(minScore);
    if (!Number.isInteger(parsedScore) || parsedScore < 0 || parsedScore > 100) {
      setError("Min score must be an integer between 0 and 100.");
      return;
    }

    try {
      const icp = await create.mutateAsync({
        name: trimmedName,
        display_name: trimmedDisplay,
        context: trimmedContext,
        description: description.trim() || null,
        min_score: parsedScore,
        is_active: false,
      });
      enqueueSnackbar(`Created ${icp.display_name}.`, { variant: "success" });
      setCreatedName(icp.name);
      setCreatedDisplayName(icp.display_name);
      setStage("samples");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const onFinishLater = () => {
    if (createdName) navigate(`/icps/${createdName}`);
  };

  const onContinueToImprove = () => {
    if (createdName) navigate(`/icps/${createdName}/improve`);
  };

  if (stage === "samples" && createdName) {
    return (
      <SamplesStage
        icpName={createdName}
        displayName={createdDisplayName}
        onFinishLater={onFinishLater}
        onContinueToImprove={onContinueToImprove}
      />
    );
  }

  return (
    <CreateForm
      name={name}
      setName={setName}
      displayName={displayName}
      setDisplayName={setDisplayName}
      context={context}
      setContext={setContext}
      description={description}
      setDescription={setDescription}
      minScore={minScore}
      setMinScore={setMinScore}
      error={error}
      submitting={create.isPending}
      onSubmit={onSubmit}
    />
  );
}

function CreateForm({
  name,
  setName,
  displayName,
  setDisplayName,
  context,
  setContext,
  description,
  setDescription,
  minScore,
  setMinScore,
  error,
  submitting,
  onSubmit,
}: {
  name: string;
  setName: (value: string) => void;
  displayName: string;
  setDisplayName: (value: string) => void;
  context: string;
  setContext: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  minScore: string;
  setMinScore: (value: string) => void;
  error: string | null;
  submitting: boolean;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <section className="card">
      <h2 style={{ marginTop: 0 }}>Create new ICP</h2>
      <p className="breadcrumb">
        <Link to="/icps">← Back to ICPs</Link>
      </p>
      <p className="card-desc">
        An ICP (Ideal Customer Profile) is a named target for lead scoring.
        This page creates the catalog row only — the AI scoring prompt and
        the Apollo search filters are configured afterwards on dedicated
        pages. New ICPs always start inactive; activate from the edit page
        once the prompt has been authored and reviewed.
      </p>

      <details className="field-hint">
        <summary>How this connects to the rest of the flow</summary>
        <ol style={{ paddingLeft: 20, margin: "8px 0" }}>
          <li>
            <strong>Now</strong> — register name, display name, qualification
            threshold. The ICP stays inactive until you flip the switch on
            the edit page; scheduled rescores and Apollo searches skip
            inactive ICPs.
          </li>
          <li>
            <strong>Next (samples)</strong> — add a few positive and negative
            example companies. Sales / domain expert decides what's a good
            fit and what isn't; negatives can carry a short "why excluded"
            note that the LLM reads when iterating.
          </li>
          <li>
            <strong>Improve prompt page</strong> — write a short free-form
            <em> context</em> describing who you're targeting, then run the
            authoring loop. The LLM drafts a v1 scoring prompt and rubric
            from your context + samples and iterates against the labels.
            Promote the best iteration to make it live.
          </li>
          <li>
            <strong>Edit page (later)</strong> — set the Apollo search
            filters (regions, organization keywords, industries) that decide
            which companies get pulled in, the Lemlist campaign id that
            qualified contacts are pushed to, and the active toggle.
          </li>
        </ol>
      </details>

      <form onSubmit={onSubmit}>
        <div className="field-row">
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="new-icp-name">Slug (immutable)</label>
            <input
              id="new-icp-name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value.toLowerCase())}
              placeholder="contech_trade_saas"
              required
              autoFocus
            />
            <p className="card-desc" style={{ fontSize: 11 }}>
              Lowercase letters, digits, underscores. Used in URLs and as the
              foreign key from <code>processed_contact_icps</code>. Cannot be
              renamed.
            </p>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="new-icp-display">Display name</label>
            <input
              id="new-icp-display"
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="ConTech / Trade SaaS"
              required
            />
            <p className="card-desc" style={{ fontSize: 11 }}>
              Human-readable name shown in lists and headings.
            </p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="new-icp-context">Context (AI-facing) *</label>
          <textarea
            id="new-icp-context"
            value={context}
            onChange={(event) => setContext(event.target.value)}
            rows={5}
            placeholder="Who does this ICP target? e.g. Cloud accounting / ERP SaaS for SMB businesses in DACH; 80–400 employees; e-invoicing / GoBD compliance focus."
            required
          />
          <p className="card-desc" style={{ fontSize: 11 }}>
            Fed to the scoring-prompt author on every iteration as the
            stable intent of this ICP. Keep it short and concrete — segment,
            size band, geography, key signals. You can revise it later on
            the edit page.
          </p>
        </div>

        <div className="field">
          <label htmlFor="new-icp-description">Description (internal notes)</label>
          <textarea
            id="new-icp-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            placeholder="Optional notes for the team — owner, history, etc."
          />
          <p className="card-desc" style={{ fontSize: 11 }}>
            For humans only — not fed to the scoring AI.
          </p>
        </div>

        <div className="field" style={{ maxWidth: 240 }}>
          <label htmlFor="new-icp-min-score">Min score (0–100)</label>
          <input
            id="new-icp-min-score"
            type="number"
            min={0}
            max={100}
            value={minScore}
            onChange={(event) => setMinScore(event.target.value)}
          />
          <p className="card-desc" style={{ fontSize: 11 }}>
            Qualification threshold. Contacts scoring at or above this are
            pushed to the Lemlist campaign on the live pipeline.
          </p>
        </div>

        {error && <StatusLine variant="error" message={error} />}

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button type="submit" disabled={submitting}>
            {submitting ? "Creating…" : "Create ICP"}
          </button>
          <Link to="/icps" className="button-link">
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}

function SamplesStage({
  icpName,
  displayName,
  onFinishLater,
  onContinueToImprove,
}: {
  icpName: string;
  displayName: string;
  onFinishLater: () => void;
  onContinueToImprove: () => void;
}) {
  return (
    <section className="card">
      <h2 style={{ marginTop: 0 }}>Add sample contacts — {displayName}</h2>
      <p className="breadcrumb">
        <Link to="/icps">← Back to ICPs</Link>
      </p>
      <p className="card-desc">
        Drop in a handful of positive and negative example companies. The
        improve-prompt loop uses these to draft and iterate the AI scoring
        prompt. You can keep editing them anytime from the ICP edit page.
        Negative samples can carry a short "why excluded" note that the LLM
        reads when iterating.
      </p>

      <IcpSampleManager icpName={icpName} />

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 16,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <button type="button" className="button-link" onClick={onFinishLater}>
          Finish later (go to edit page)
        </button>
        <button type="button" onClick={onContinueToImprove}>
          Continue to improve prompt →
        </button>
      </div>
    </section>
  );
}
